// Stockfish engine wrapper for browser (WASM)
// Provides a simple API to get best move with accurate UCI_Elo control
import { TimeoutError, WorkerCrashError, EngineNotReadyError, getBackoffDelay } from './engine-errors';

const STOCKFISH_LOCAL_URL = '/workers/stockfish.js';

export type StockfishInstance = {
  postMessage: (cmd: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onmessage: ((event: { data: any }) => void) | null;
  terminate?: () => void;
};

export interface StockfishOptions {
  elo?: number; // Use Stockfish UCI_LimitStrength + UCI_Elo for approximate Elo
  skillLevel?: number; // 0-20 legacy option
  movetime?: number; // milliseconds
  depth?: number; // search depth
  multiPV?: number; // Number of principal variations to analyze (1-500)
}

export interface EngineVariation {
  move: string; // Best move in UCI format
  evaluation: number; // Evaluation in centipawns
  isMate: boolean;
  mateIn?: number;
  pv: string[]; // Principal variation moves
  depth: number;
}

export const STOCKFISH_UCI_ELO_MIN = 1350;
export const STOCKFISH_UCI_ELO_MAX = 2850;

export function clampStockfishElo(val: number): number {
  return Math.max(STOCKFISH_UCI_ELO_MIN, Math.min(val, STOCKFISH_UCI_ELO_MAX));
}

/**
 * Create Stockfish worker instance (singleton pattern)
 */
function createStockfishWorker(): StockfishInstance {
  const g = globalThis as unknown as { __SF_WORKER?: StockfishInstance };
  if (g.__SF_WORKER) return g.__SF_WORKER;
  
  try {
    const url = new URL(STOCKFISH_LOCAL_URL, globalThis.location?.href).href;
    const worker = new Worker(url, { name: 'stockfish-loader', type: 'classic' });
    g.__SF_WORKER = worker as unknown as StockfishInstance;
    return g.__SF_WORKER;
  } catch (e) {
    // Surface a clearer error
    throw new Error(`Failed to initialize Stockfish worker from ${STOCKFISH_LOCAL_URL}: ${(e as Error)?.message || e}`);
  }
}

/**
 * Get the detected Stockfish source for timeout adjustments
 */
export function getStockfishSource(): string {
  const g = globalThis as unknown as { __SF_SOURCE?: string };
  return g.__SF_SOURCE || 'unknown';
}

export function ensureStockfishReady(): Promise<void> {
  const g = globalThis as unknown as { __SF_WORKER?: StockfishInstance; __SF_INIT_PROMISE?: Promise<void>; __SF_SOURCE?: string; __SF_RETRY_COUNT?: number };
  if (g.__SF_INIT_PROMISE) return g.__SF_INIT_PROMISE;

  g.__SF_INIT_PROMISE = new Promise<void>(async (resolve, reject) => {
    try {
      const worker = createStockfishWorker();
      
      // Start retry count
      g.__SF_RETRY_COUNT = g.__SF_RETRY_COUNT || 0;
      
      const timeoutMs = 15000; // 15-second timeout for initialization
      let resolved = false;
      
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          reject(new Error('Stockfish initialization timeout'));
        }
      }, timeoutMs);
      
      const handleMessage = (event: { data: string }) => {
        const line = event.data;
        
        if (line?.startsWith?.('sf_loader:source=')) {
          const source = line.split('=')[1];
          g.__SF_SOURCE = source;
          try { console.info(`[Stockfish] Detected source: ${source}`); } catch {}
        }
        
        if (line === 'sf_loader:bridge-ready' && !resolved) {
          resolved = true;
          clearTimeout(timeout);
          // Reset retry count on success
          g.__SF_RETRY_COUNT = 0;
          try { console.info('[Stockfish] Engine ready'); } catch {}
          resolve();
        }
        
        if (line?.startsWith?.('sf_loader:error') && !resolved) {
          resolved = true;
          clearTimeout(timeout);
          reject(new Error(`Stockfish loader error: ${line}`));
        }
      };
      
      worker.onmessage = handleMessage;
      
    } catch (error) {
      reject(error);
    }
  });
  
  return g.__SF_INIT_PROMISE;
}

/**
 * Preload and warm up Stockfish engine
 */
export async function prewarmStockfish(): Promise<void> {
  return ensureStockfishReady();
}

/**
 * Reset Stockfish engine (terminate and clear cache)
 */
export function resetStockfish(): void {
  const g = globalThis as unknown as { __SF_WORKER?: StockfishInstance; __SF_INIT_PROMISE?: Promise<void>; __SF_SOURCE?: string };
  try {
    if (g.__SF_WORKER?.terminate) {
      g.__SF_WORKER.terminate();
    }
  } catch (error) {
    try { console.warn('[Stockfish] Reset error:', error); } catch {}
  } finally {
    g.__SF_WORKER = undefined;
    g.__SF_INIT_PROMISE = undefined;
    g.__SF_SOURCE = undefined;
  }
  
  // Clear localStorage cache as well
  try { if (typeof localStorage !== 'undefined') localStorage.removeItem('__SF_SOURCE'); } catch {}
}

// Best-effort preemptive stop that does not wait for the lock
export function softStopEngine(): void {
  try {
    const g = globalThis as unknown as { __SF_WORKER?: StockfishInstance };
    const engine = g.__SF_WORKER;
    if (engine && typeof engine.postMessage === 'function') {
      engine.postMessage('stop');
      engine.postMessage('isready'); // Ensure engine acknowledges stop
    }
  } catch {}
}

function waitFor(engine: StockfishInstance, token: string, timeoutMs = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    let resolved = false;
    
    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        console.error(`[Stockfish] Timeout waiting for: ${token}`);
        reject(new Error(`Stockfish timeout waiting for: ${token}`));
      }
    }, timeoutMs);

    const handler = (e: { data: unknown }) => {
      if (resolved) return; // Ignore if already resolved
      
      const line = typeof e.data === 'string' ? e.data : '';
      console.log(`[Stockfish] ← ${line.substring(0, 100)}`); // Log first 100 chars
      
      if (line.includes(token)) {
        resolved = true;
        cleanup();
        resolve();
      }
    };

    const cleanup = () => {
      clearTimeout(timeout);
      try {
        if ((engine as any).removeEventListener) {
          (engine as any).removeEventListener('message', handler);
        } else {
          // Only clear if it's our handler
          if (engine.onmessage === handler) {
            engine.onmessage = null;
          }
        }
      } catch (e) {
        console.warn('[Stockfish] Cleanup error:', e);
      }
    };

    // Use addEventListener if available (preferred for proper cleanup)
    if ((engine as any).addEventListener) {
      (engine as any).addEventListener('message', handler);
    } else {
      // For older workers, just replace the handler
      engine.onmessage = handler;
    }
  });
}

export async function getBestMoveFromStockfish(
  fen: string,
  { elo, skillLevel, movetime = 1000, depth }: StockfishOptions = {}
): Promise<string> {
  return withEngineLock(
    async () => {
    // Ensure engine is initialized and available
    await ensureStockfishReady();
    const engine = createStockfishWorker();

    // Helper to send commands
    const send = (cmd: string) => {
      try { console.info(`[Stockfish] → ${cmd}`); } catch {}
      engine.postMessage(cmd);
    };

    // Ensure UCI mode and sync ready state - PROPER SEQUENCE
    send('uci');
    await waitFor(engine, 'uciok', 5000); // Wait for UCI initialization
    
    // Small delay to let engine stabilize
    await new Promise(resolve => setTimeout(resolve, 50));
    
    send('isready');
    await waitFor(engine, 'readyok', 5000); // Wait for ready confirmation

    // Prepare new game (clear transposition table, reset state)
    send('ucinewgame');
    
    // Strength controls
    if (typeof elo === 'number') {
      const clampedElo = clampStockfishElo(elo);
      send('setoption name UCI_LimitStrength value true');
      send(`setoption name UCI_Elo value ${clampedElo}`);
      try { console.info(`[Stockfish] UCI_LimitStrength=true, UCI_Elo=${clampedElo}`); } catch {}
    }
    if (typeof skillLevel === 'number') {
      // 0-20
      const clampedSkill = Math.max(0, Math.min(skillLevel, 20));
      send(`setoption name Skill Level value ${clampedSkill}`);
      try { console.info(`[Stockfish] Skill Level=${clampedSkill}`); } catch {}
    }

    // Position
    send(`position fen ${fen}`);

    // Prefer movetime for consistent performance
    const clampedMovetime = Math.max(800, Math.min(movetime, 10000));
    send(`go movetime ${clampedMovetime}`);

    // Wait for best move
      const bestmove = await new Promise<string>((resolve, reject) => {
        let watchdog: ReturnType<typeof setTimeout> | null = null;
        const cleanup = () => {
          if (watchdog) {
            clearTimeout(watchdog);
            watchdog = null;
          }
          clearTimeout(timeout);
          try {
            if ((engine as any).removeEventListener) {
              (engine as any).removeEventListener('message', handler);
            } else {
              engine.onmessage = null;
            }
          } catch {}
        };

        const timeout = setTimeout(() => {
          try { engine.postMessage('stop'); } catch {}
          cleanup();
          reject(new Error('Stockfish bestmove timeout'));
        }, clampedMovetime + 2000); // Reduced timeout buffer

        // Watchdog to nudge the engine to finish
        watchdog = setTimeout(() => {
          try { engine.postMessage('stop'); } catch {}
        }, Math.max(0, clampedMovetime + 500));

        const handler = (e: { data: unknown }) => {
          const line = typeof e.data === 'string' ? e.data : '';
          if (line.startsWith('bestmove')) {
            const parts = line.split(' ');
            const move = parts[1];
            cleanup();
            try { console.info(`[Stockfish] ← ${line}`); } catch {}
            resolve(move);
          }
        };

        // Use addEventListener if available (for proper Stockfish package), otherwise fallback to onmessage
        if ((engine as any).addEventListener) {
          (engine as any).addEventListener('message', handler);
        } else {
          const prev = engine.onmessage;
          engine.onmessage = (e) => {
            prev?.(e as unknown as { data: string });
            handler(e as unknown as { data: string });
          };
        }
      });

    return bestmove;
  },
  undefined, // signal
  EnginePriority.CRITICAL,
  'getBestMove'
  );
}

/**
 * Gets position evaluation from Stockfish
 * Returns evaluation in centipawns (positive = white advantage)
 */
export async function getPositionEvaluation(
  fen: string, 
  depth: number = 15,
  signal?: AbortSignal
): Promise<number> {
  return withEngineLock(async () => {
    let retries = 0;
    const maxRetries = 1;
    
    while (retries <= maxRetries) {
      try {
        await ensureStockfishReady();
        
        const engine = createStockfishWorker();
        
        const send = (cmd: string) => {
          try { console.info(`[Stockfish] → ${cmd}`); } catch {}
          engine.postMessage(cmd);
        };
        
        // Stop any ongoing search and ensure engine is ready
        send('stop');
        await waitForReady(engine, 10000, signal);
    
    return new Promise((resolve, reject) => {
      // Check if already aborted
      if (signal?.aborted) {
        send('stop');
        throw new DOMException('Evaluation aborted', 'AbortError');
      }
      
      let evaluation = 0;
      let mateScore: number | null = null;
      
      const cleanup = () => {
        clearTimeout(timeout);
        try {
          if ((engine as any).removeEventListener) {
            (engine as any).removeEventListener('message', handler);
          } else {
            engine.onmessage = null;
          }
        } catch {}
      };
      
      const timeout = setTimeout(() => {
        try { send('stop'); } catch {}
        cleanup();
        reject(new Error('Stockfish evaluation timeout'));
      }, Math.max(2500, depth * 500 + 1500));

      const handler = (e: { data: unknown }) => {
        const line = typeof e.data === 'string' ? e.data : '';
        
        if (signal?.aborted) {
          try { send('stop'); } catch {}
          cleanup();
          return;
        }
        
        // Parse engine evaluation info
        if (line.startsWith('info') && line.includes('score')) {
          const cpMatch = line.match(/score cp (-?\d+)/);
          const mateMatch = line.match(/score mate (-?\d+)/);
          
          if (mateMatch) {
            const mateInMoves = parseInt(mateMatch[1]);
            // Convert mate-in-N to centipawn equivalent (high values)
            mateScore = mateInMoves > 0 ? 29900 + mateInMoves : -29900 - mateInMoves;
          } else if (cpMatch) {
            evaluation = parseInt(cpMatch[1]);
          }
        }
        
        if (line.startsWith('bestmove')) {
          cleanup();
          
          // Return mate score if found, otherwise centipawn evaluation
          resolve(mateScore !== null ? mateScore : evaluation);
        }
      };

      try {
        if ((engine as any).addEventListener) {
          (engine as any).addEventListener('message', handler);
        } else {
          const prev = engine.onmessage;
          engine.onmessage = (e) => {
            prev?.(e as unknown as { data: string });
            handler(e as unknown as { data: string });
          };
        }

        // Configure engine
        send('setoption name Hash value 128');
        send('setoption name Threads value 1');
        send('ucinewgame');
        send(`position fen ${fen}`);
        
        // Start analysis with either depth or time limit
        if (depth && depth > 0) {
          send(`go depth ${depth}`);
        } else {
          send(`go movetime 2000`); // Default movetime for evaluation
        }

      } catch (err) {
        cleanup();
        reject(err as Error);
      }

    });
      } catch (error) {
        retries++;
        if (retries <= maxRetries && !signal?.aborted) {
          console.warn(`[SF Evaluation] Attempt ${retries} failed, resetting engine...`, error);
          try {
            resetStockfish();
            await ensureStockfishReady();
          } catch (resetError) {
            console.error('[SF Evaluation] Engine reset failed:', resetError);
            throw error; // Re-throw original error
          }
        } else {
          throw error;
        }
      }
    }
    
    throw new Error('Evaluation failed after all retries');
  }, signal, EnginePriority.HIGH, 'positionEval');
}

/**
 * Analyzes a position and returns detailed evaluation info
 */
export interface PositionAnalysis {
  evaluation: number; // centipawns
  bestMove: string | null; // UCI notation
  isMate: boolean;
  mateIn?: number; // moves to mate (if applicable)
  depth: number; // analysis depth achieved
  variations: EngineVariation[]; // Top variations (MultiPV)
}

// Priority levels for engine requests
export enum EnginePriority {
  CRITICAL = 0, // User-initiated actions (e.g., getting best move during game)
  HIGH = 1,     // Live analysis of current position
  NORMAL = 2    // Game review, background tasks
}

interface QueuedRequest<T> {
  execute: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (error: unknown) => void;
  priority: EnginePriority;
  timestamp: number;
  source: string;
  signal?: AbortSignal;
}

// Priority queue with size limit
let engineBusy = false;
const MAX_QUEUE_SIZE = 5;
const engineQueue: QueuedRequest<unknown>[] = [];

function sortQueue() {
  // Sort by priority first (lower number = higher priority), then by timestamp (FIFO within priority)
  engineQueue.sort((a, b) => {
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.timestamp - b.timestamp;
  });
}

async function withEngineLock<T>(
  operation: () => Promise<T>, 
  signal?: AbortSignal,
  priority: EnginePriority = EnginePriority.NORMAL,
  source: string = 'unknown'
): Promise<T> {
  return new Promise((resolve, reject) => {
    const request: QueuedRequest<T> = {
      execute: operation,
      resolve: resolve as (value: unknown) => void,
      reject,
      priority,
      timestamp: Date.now(),
      source,
      signal
    };

    const processRequest = async () => {
      // Early exit if operation was cancelled while queued
      if (request.signal?.aborted) {
        request.reject(new DOMException('Operation cancelled', 'AbortError'));
        return;
      }
      
      if (engineBusy) {
        // Check queue size limit
        if (engineQueue.length >= MAX_QUEUE_SIZE) {
          // Find lowest priority request to drop
          const lowestPriorityIndex = engineQueue.reduce((minIdx, curr, idx, arr) => 
            curr.priority > arr[minIdx].priority ? idx : minIdx
          , 0);
          
          const dropped = engineQueue[lowestPriorityIndex];
          
          // If new request has same or lower priority than lowest in queue, drop the new one
          if (request.priority >= dropped.priority) {
            console.warn(`[Engine Queue] Dropped ${source} request (priority: ${priority}). Queue full.`);
            request.reject(new Error(`Engine queue is full. Request dropped. Please wait and try again.`));
            return;
          }
          
          // Drop the lowest priority request from queue
          engineQueue.splice(lowestPriorityIndex, 1);
          console.warn(`[Engine Queue] Dropped ${dropped.source} request (priority: ${dropped.priority}) to make room for ${source}`);
          dropped.reject(new Error(`Engine queue is full. Lower priority request dropped.`));
        }
        
        // Add to queue and sort by priority
        engineQueue.push(request as QueuedRequest<unknown>);
        sortQueue();
        console.info(`[Engine Queue] Queued ${source} (priority: ${priority}). Queue size: ${engineQueue.length}`);
        return;
      }
      
      // Execute immediately
      engineBusy = true;
      console.info(`[Engine Queue] Executing ${source} (priority: ${priority})`);
      
      try {
        const result = await request.execute();
        request.resolve(result);
      } catch (error) {
        request.reject(error);
      } finally {
        engineBusy = false;
        
        // Process next request in queue
        const next = engineQueue.shift();
        if (next) {
          console.info(`[Engine Queue] Processing next from queue. Remaining: ${engineQueue.length}`);
          // Process next request asynchronously
          setTimeout(async () => {
            // Check if next request was cancelled while waiting
            if (next.signal?.aborted) {
              next.reject(new DOMException('Operation cancelled', 'AbortError'));
              // Process the one after it
              engineBusy = false;
              const afterNext = engineQueue.shift();
              if (afterNext) {
                setTimeout(() => processNextRequest(afterNext), 0);
              }
              return;
            }
            
            await processNextRequest(next);
          }, 0);
        }
      }
    };
    
    const processNextRequest = async (req: QueuedRequest<unknown>) => {
      engineBusy = true;
      console.info(`[Engine Queue] Executing ${req.source} (priority: ${req.priority})`);
      
      try {
        const result = await req.execute();
        req.resolve(result);
      } catch (error) {
        req.reject(error);
      } finally {
        engineBusy = false;
        
        // Continue processing queue
        const next = engineQueue.shift();
        if (next) {
          console.info(`[Engine Queue] Processing next from queue. Remaining: ${engineQueue.length}`);
          setTimeout(() => processNextRequest(next), 0);
        }
      }
    };
    
    processRequest();
  });
}

// Export queue stats for debugging and UI
export function getEngineQueueStats() {
  return {
    isBusy: engineBusy,
    queueSize: engineQueue.length,
    maxQueueSize: MAX_QUEUE_SIZE,
    queued: engineQueue.map(req => ({
      priority: req.priority,
      source: req.source,
      timestamp: req.timestamp,
      waitTime: Date.now() - req.timestamp
    }))
  };
}

// Robust waitForReady that handles race conditions with cancellation support
async function waitForReady(engine: StockfishInstance, timeoutMs = 10000, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    let messageHandler: ((e: { data: unknown }) => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    const maxAttempts = 3;
    
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
      if (messageHandler) {
        try {
          if ((engine as any).removeEventListener) {
            (engine as any).removeEventListener('message', messageHandler);
          } else {
            engine.onmessage = null;
          }
        } catch {}
        messageHandler = null;
      }
    };

    // Check if already aborted
    if (signal?.aborted) {
      reject(new DOMException('Operation cancelled', 'AbortError'));
      return;
    }

    const attemptReadiness = () => {
      attempts++;
      
      messageHandler = (e: { data: unknown }) => {
        const line = typeof e.data === 'string' ? e.data : '';
        if (line === 'readyok') {
          cleanup();
          resolve();
        }
      };
      
      timeoutId = setTimeout(() => {
        if (attempts < maxAttempts && !signal?.aborted) {
          // Try again with a fresh approach
          cleanup();
          setTimeout(attemptReadiness, 200);
        } else {
          cleanup();
          reject(new Error(`waitForReady timeout after ${attempts} attempts`));
        }
      }, timeoutMs / maxAttempts);
      
      try {
        if ((engine as any).addEventListener) {
          (engine as any).addEventListener('message', messageHandler);
        } else {
          const prev = engine.onmessage;
          engine.onmessage = (e) => {
            prev?.(e as unknown as { data: string });
            messageHandler?.(e as unknown as { data: string });
          };
        }
        
        // Multi-phase readiness check
        setTimeout(() => {
          try {
            if (signal?.aborted) return;
            
            engine.postMessage('stop'); // Ensure any previous operation is stopped
            setTimeout(() => {
              if (signal?.aborted) return;
              engine.postMessage('uci'); // Enter UCI mode
              setTimeout(() => {
                if (signal?.aborted) return;
                engine.postMessage('isready'); // Request readiness
              }, 100);
            }, 50);
          } catch (error) {
            cleanup();
            reject(error);
          }
        }, 50);
        
      } catch (error) {
        cleanup();
        reject(error);
      }
    };

    // Handle signal cancellation
    if (signal) {
      signal.addEventListener('abort', () => {
        cleanup();
        reject(new DOMException('Operation cancelled', 'AbortError'));
      });
    }

    attemptReadiness();
  });
}

export async function analyzePosition(
  fen: string, 
  options: StockfishOptions = {},
  signal?: AbortSignal
): Promise<PositionAnalysis> {
  return withEngineLock(async () => {
    let retries = 0;
    const maxRetries = 1;
    
    while (retries <= maxRetries) {
      try {
        await ensureStockfishReady();
        
        const engine = createStockfishWorker();
        const { depth = 12, movetime = 3000, multiPV = 3 } = options;
        
        const send = (cmd: string) => {
          try {
            console.log(`[SF Analysis] > ${cmd}`);
            engine.postMessage(cmd);
          } catch (err) {
            console.error('[SF Analysis] Send failed:', err);
            throw err;
          }
        };
        
        // Stop any ongoing search and ensure engine is ready
        send('stop');
        await waitForReady(engine, 10000, signal);
    
    return new Promise((resolve, reject) => {
      // Check if already aborted
      if (signal?.aborted) {
        send('stop');
        throw new DOMException('Analysis aborted', 'AbortError');
      }
      
      let evaluation = 0;
      let bestMove: string | null = null;
      let isMate = false;
      let mateIn: number | undefined;
      let achievedDepth = 0;
      const variations: EngineVariation[] = [];
      
      // Timeout and watchdog adjusted dynamically for MultiPV
      const timeoutMs = Math.max(3000, (movetime || 3000) * (multiPV > 1 ? 3 : 1) + 1500);
      const timeout = setTimeout(() => {
        try { send('stop'); send('ucinewgame'); } catch {}
        cleanup();
        reject(new TimeoutError(timeoutMs));
      }, timeoutMs);
      let watchdog: ReturnType<typeof setTimeout> | null = null;
      watchdog = setTimeout(() => {
        try { send('stop'); } catch {}
      }, Math.max(0, (movetime || 3000) * (multiPV > 1 ? 3 : 1))); // Scale with MultiPV
      
      const handler = (e: { data: unknown }) => {
        const line = typeof e.data === 'string' ? e.data : '';
        
        if (signal?.aborted) {
          try { send('stop'); send('ucinewgame'); } catch {}
          cleanup();
          reject(new DOMException('Analysis aborted', 'AbortError'));
          return;
        }
        
        // Parse info lines for evaluation and variations
        if (line.startsWith('info') && line.includes('depth')) {
          const depthMatch = line.match(/depth (\d+)/);
          if (depthMatch) {
            achievedDepth = Math.max(achievedDepth, parseInt(depthMatch[1]));
          }
          
          if (line.includes('score')) {
            const multiPVMatch = line.match(/multipv (\d+)/);
            const pvIndex = multiPVMatch ? parseInt(multiPVMatch[1]) - 1 : 0;
            
            const cpMatch = line.match(/score cp (-?\d+)/);
            const mateMatch = line.match(/score mate (-?\d+)/);
            const pvMatch = line.match(/pv (.+)$/);
            
            let score = 0;
            let isVariationMate = false;
            let variationMateIn: number | undefined;
            
            if (mateMatch) {
              variationMateIn = parseInt(mateMatch[1]);
              score = variationMateIn > 0 ? 29900 + variationMateIn : -29900 - variationMateIn;
              isVariationMate = true;
              
              // Set main position mate info for first variation
              if (pvIndex === 0) {
                isMate = true;
                mateIn = Math.abs(variationMateIn);
              }
            } else if (cpMatch) {
              score = parseInt(cpMatch[1]);
            }
            
            // Set main evaluation from first variation
            if (pvIndex === 0) {
              evaluation = score;
            }
            
            // Store variation
            if (pvMatch) {
              const moves = pvMatch[1].split(' ').filter(m => m.length > 0);
              if (moves.length > 0) {
                const variation: EngineVariation = {
                  move: moves[0],
                  evaluation: score,
                  isMate: isVariationMate,
                  mateIn: variationMateIn ? Math.abs(variationMateIn) : undefined,
                  pv: moves,
                  depth: achievedDepth
                };
                
                // Ensure variations array is large enough
                while (variations.length <= pvIndex) {
                  variations.push({
                    move: '',
                    evaluation: 0,
                    isMate: false,
                    pv: [],
                    depth: 0
                  });
                }
                
                variations[pvIndex] = variation;
                
                // Set best move from first variation
                if (pvIndex === 0) {
                  bestMove = moves[0];
                }
              }
            }
          }
        }
        
        if (line.startsWith('bestmove')) {
          cleanup();
          
          // If no best move was captured from PV, extract from bestmove line
          if (!bestMove) {
            const parts = line.split(' ');
            bestMove = parts[1] || null;
          }
          
          resolve({
            evaluation,
            bestMove,
            isMate,
            mateIn,
            depth: achievedDepth,
            variations: variations.filter(v => v.move) // Filter out empty variations
          });
        }
      };
      
      const cleanup = () => {
        if (watchdog) {
          clearTimeout(watchdog);
          watchdog = null;
        }
        clearTimeout(timeout);
        try {
          if ((engine as any).removeEventListener) {
            (engine as any).removeEventListener('message', handler);
          } else {
            engine.onmessage = null;
          }
        } catch {}
      };
      
      try {
        // Set up message handler
        if ((engine as any).addEventListener) {
          (engine as any).addEventListener('message', handler);
        } else {
          const prev = engine.onmessage;
          engine.onmessage = (e) => {
            prev?.(e as unknown as { data: string });
            handler(e as unknown as { data: string });
          };
        }
        
        // Configure engine for analysis
        send('setoption name Hash value 128');
        send('setoption name Threads value 1');
        
        // Enable MultiPV for multiple variations
        if (multiPV > 1) {
          send(`setoption name MultiPV value ${Math.min(multiPV, 5)}`);
        }
        
        send('ucinewgame');
        send(`position fen ${fen}`);
        
        // Start analysis - prefer movetime for predictable completion
        if (movetime) {
          send(`go movetime ${movetime}`);
        } else {
          send(`go depth ${depth}`);
        }
        
      } catch (err) {
        cleanup();
        reject(err);
      }
    });
      } catch (error) {
        retries++;
        if (retries <= maxRetries && !signal?.aborted) {
          const backoffDelay = getBackoffDelay(retries, 500);
          console.warn(`[SF Analysis] Attempt ${retries} failed, waiting ${backoffDelay}ms before retry...`, error);
          
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          
          try {
            resetStockfish();
            await ensureStockfishReady();
          } catch (resetError) {
            console.error('[SF Analysis] Engine reset failed:', resetError);
            throw new WorkerCrashError(error);
          }
        } else {
          throw error instanceof Error ? error : new TimeoutError(30000, error);
        }
      }
    }
    
    throw new EngineNotReadyError(new Error('Analysis failed after all retries'));
  }, signal, EnginePriority.HIGH, 'analyzePosition');
}